import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, Button, Theme, useMediaQuery } from "@mui/material";
import { useAccountsId } from "@rentablo/api/apiv1";
import { useUpdateAccount } from "@api/v1/hooks/useAccounts";
import { getUpdateBankConnectionUrl } from "@helpers";
import { ActionIconButton, ResponsiveDropdown } from "@components";
import { ConfirmDeleteDialog } from "@components";
import { useDeleteAccount } from "@api/v1/hooks/useAccounts";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import DotsIcon from "@icons/myicons/dots.svg?react";
import { useSingleAccountMenuItems } from "./useSingleAccountMenuItems";

interface Props {
  accountId: number;
}

export const SingleAccountPageActions = ({ accountId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { data: account } = useAccountsId({ id: accountId });
  const {
    mutateAsync: deleteAccount,
    reset: resetDeleteAccountResult,
    isPending: isDeleteAccountLoading,
    isError: isDeleteAccountError,
    error: deleteAccountError,
  } = useDeleteAccount();
  const { mutateAsync: updateAccount } = useUpdateAccount();

  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const updateConnectionUrl = getUpdateBankConnectionUrl(account);
  const primaryActionUrl =
    updateConnectionUrl ?? `/accounts/${accountId}/transactions/add-manually`;
  const primaryLabel = t(
    updateConnectionUrl
      ? "singleAccountPage.header.update"
      : "singleAccountPage.header.addTransaction"
  );
  const primaryIcon = updateConnectionUrl ? (
    <UpdateIcon height="22px" width="22px" />
  ) : (
    <AddIcon width="18px" height="12px" />
  );

  const handleDeleteAccount = async () => {
    await deleteAccount(accountId);
    navigate("/accounts");
  };

  const handleHideAccount = async () => {
    await updateAccount({
      id: accountId,
      isHidden: !account!.isHidden,
      type: account!.type!,
      name: account!.name,
    });
    navigate("/accounts");
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalShow(false);

    if (isDeleteAccountError) {
      resetDeleteAccountResult();
    }
  };

  const menuItems = useSingleAccountMenuItems({
    account,
    handleHideAccount,
    handleOpenDeleteModal: () => setDeleteModalShow(true),
  });

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        {!isSm && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={primaryIcon}
            onClick={() => navigate(primaryActionUrl)}
          >
            {primaryLabel}
          </Button>
        )}
        <ResponsiveDropdown
          id="single-account-actions-menu"
          mobileTitle={account?.name}
          items={menuItems}
          anchor={
            <ActionIconButton
              data-testid="open-menu-button"
              color="tertiary"
              size="small"
            >
              <DotsIcon />
            </ActionIconButton>
          }
        />
      </Box>
      <ConfirmDeleteDialog
        isOpen={deleteModalShow}
        title={t("depots.deleteModal.question")}
        isDeleteRequestLoading={isDeleteAccountLoading}
        errorMessage={deleteAccountError?.response?.data?.message}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteAccount}
      />
    </>
  );
};
